import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Avatar,
  Chip,
  Box,
  TablePagination,
  IconButton,
  Tooltip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  CheckCircle as UnblockIcon,
  Person as PersonIcon
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../config/config';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
  },
  searchContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
  },
  searchInput: {
    flexGrow: 1,
  },
  avatar: {
    width: 40,
    height: 40,
  },
  onlineChip: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  offlineChip: {
    backgroundColor: '#f44336',
    color: 'white',
  },
  blockedChip: {
    backgroundColor: '#ff9800',
    color: 'white',
  },
  actionButton: {
    marginLeft: theme.spacing(1),
  },
}));

const api = axios.create({
  baseURL: config.API.BASE_URL,
  timeout: config.API.TIMEOUT,
});

const UserTablePage = () => {
  const classes = useStyles();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Filter users based on search term
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter((user) =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.uniqueID?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
    setPage(0); // Reset to first page when searching
  }, [users, searchTerm]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem(config.SECURITY.TOKEN_KEY);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const response = await api.get('/user');
      if (response.data.status) {
        setUsers(response.data.users || []);
      } else {
        toast.error('Error loading users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error loading users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRefresh = () => {
    fetchUsers();
  };

  const handleToggleBlock = async (userId, isCurrentlyBlocked) => {
    try {
      const token = localStorage.getItem(config.SECURITY.TOKEN_KEY);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const response = await api.patch(`/user/${userId}/block`, {
        is_block: !isCurrentlyBlocked
      });
      
      if (response.data.status) {
        toast.success(`User ${!isCurrentlyBlocked ? 'blocked' : 'unblocked'} successfully`);
        fetchUsers(); // Refresh the list
      } else {
        toast.error('Error updating user status');
      }
    } catch (error) {
      console.error('Error toggling user block:', error);
      toast.error('Error updating user status');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusChip = (user) => {
    if (user.isBlock) {
      return <Chip label="Blocked" className={classes.blockedChip} size="small" />;
    }
    if (user.isOnline) {
      return <Chip label="Online" className={classes.onlineChip} size="small" />;
    }
    return <Chip label="Offline" className={classes.offlineChip} size="small" />;
  };

  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Users Management
      </Typography>

      <Paper className={classes.paper}>
        {/* Search and Actions */}
        <Box className={classes.searchContainer}>
          <TextField
            className={classes.searchInput}
            variant="outlined"
            size="small"
            placeholder="Search by name, email, or unique ID..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: <SearchIcon color="action" />,
            }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Users Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Unique ID</TableCell>
                <TableCell>Coins</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Loading users...
                  </TableCell>
                </TableRow>
              ) : paginatedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell>{user._id || user.id || 'N/A'}</TableCell>
                    <TableCell>{user.name || 'N/A'}</TableCell>
                    <TableCell>{user.email || 'N/A'}</TableCell>
                    <TableCell>{user.uniqueID || 'N/A'}</TableCell>
                    <TableCell>{user.coin || 0}</TableCell>
                    <TableCell>{getStatusChip(user)}</TableCell>
                    <TableCell>
                      <Tooltip title={user.isBlock ? 'Unblock User' : 'Block User'}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleBlock(user._id, user.isBlock)}
                          color={user.isBlock ? 'primary' : 'secondary'}
                        >
                          {user.isBlock ? <UnblockIcon /> : <BlockIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
};

export default UserTablePage;